/**
 * Screens: one row per TV in the estate.
 *
 * There is no "add screen" button and there never will be. A screen exists
 * because a TV showed a pairing code and somebody claimed it; inventing one here
 * would create a record no device could ever attach to. The page says so where
 * the button would have been.
 *
 * The list refreshes on a timer shorter than the device heartbeat, so a stick
 * that drops off shows as offline within about one heartbeat of the server
 * deciding it has, rather than whenever somebody happens to reload.
 */

import { useMemo, useState } from 'react';
import type { ReactElement } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { HEARTBEAT_SEC } from '@pbtv/shared';
import type { FleetScreen, RotationDegrees, ScreenSchedule } from '@pbtv/shared';
import { PageHeader } from '../components/PageHeader.js';
import { Modal } from '../components/Modal.js';
import { ConfirmDialog } from '../components/ConfirmDialog.js';
import { ErrorBlock, InfoNote, InlineEmpty, LoadingBlock } from '../components/Feedback.js';
import { ClaimScreenDialog } from '../components/screens/ClaimScreenDialog.js';
import { AssignPlaylistForm } from '../components/screens/AssignPlaylistForm.js';
import { ScreenEditForm } from '../components/screens/ScreenEditForm.js';
import { ScreenPreview } from '../components/preview/ScreenPreview.js';
import { rotationLabel } from '../components/preview/geometry.js';
import type { PreviewResolution } from '../components/preview/geometry.js';
import { sampleManifest } from '../components/preview/sampleManifest.js';
import type { PreviewScenario } from '../components/preview/sampleManifest.js';
import { errorMessage } from '../lib/api.js';
import { canEditScreen, hasNoScope } from '../lib/access.js';
import { sendCommand } from '../lib/commands.js';
import type { CommandDelivery } from '../lib/commands.js';
import {
  formatAbsoluteTime,
  formatAppVersion,
  formatRelativeTime,
} from '../lib/format.js';
import {
  FLEET_QUERY_KEY,
  deleteScreen,
  listFleet,
  screenQueryKey,
  setScreenSchedule,
  updateScreen,
} from '../lib/screens.js';
import type { ScreenPatch } from '../lib/screens.js';
import { PLAYLISTS_QUERY_KEY, listPlaylistsAcross } from '../lib/playlists.js';
import {
  healthExplanation,
  healthLabel,
  healthTone,
  playbackStateLabel,
  screenFlags,
  sortFleet,
  storageSummary,
  tallyFleet,
} from '../lib/screenPresentation.js';
import type { FleetSort } from '../lib/screenPresentation.js';
import { useSession } from '../lib/session.js';
import { useNow } from '../lib/useNow.js';

/**
 * Deliberately shorter than one heartbeat: whatever the server knows, this page
 * is holding it within half a device cycle.
 */
const FLEET_REFRESH_MS = Math.round((HEARTBEAT_SEC * 1000) / 1.5);

const SORTS: readonly { value: FleetSort; label: string }[] = [
  { value: 'attention', label: 'Needs attention' },
  { value: 'store', label: 'By store' },
];

const RESOLUTIONS: readonly { label: string; value: PreviewResolution }[] = [
  { label: '1080p · 1920 × 1080', value: { width: 1920, height: 1080 } },
  { label: '720p · 1280 × 720', value: { width: 1280, height: 720 } },
  { label: '4K · 3840 × 2160', value: { width: 3840, height: 2160 } },
];

const SCENARIOS: readonly { value: PreviewScenario; label: string }[] = [
  { value: 'playing', label: 'Open' },
  { value: 'closed', label: 'Outside hours' },
  { value: 'idle', label: 'Nothing assigned' },
];

type Dialog =
  | { kind: 'none' }
  | { kind: 'edit'; row: FleetScreen }
  | { kind: 'assign'; row: FleetScreen }
  | { kind: 'update'; row: FleetScreen }
  | { kind: 'remove'; row: FleetScreen };

export function ScreensPage(): ReactElement {
  const { user } = useSession();
  const queryClient = useQueryClient();
  const now = useNow();

  const [sort, setSort] = useState<FleetSort>('attention');
  const [dialog, setDialog] = useState<Dialog>({ kind: 'none' });
  const [claiming, setClaiming] = useState(false);
  const closeDialog = (): void => setDialog({ kind: 'none' });

  const fleet = useQuery({
    queryKey: FLEET_QUERY_KEY,
    queryFn: ({ signal }) => listFleet(signal),
    refetchInterval: FLEET_REFRESH_MS,
  });

  const update = useMutation({
    mutationFn: ({ screenId, patch }: { screenId: string; patch: ScreenPatch }) =>
      updateScreen(screenId, patch),
    onSuccess: async (_result, variables) => {
      await queryClient.invalidateQueries({ queryKey: FLEET_QUERY_KEY });
      await queryClient.invalidateQueries({ queryKey: screenQueryKey(variables.screenId) });
      closeDialog();
    },
  });

  // Every library this account can reach, so a screen can be pointed at its own
  // store's playlist or a shared one. Shares the playlists page's cache.
  const playlistScopes = useMemo<(string | null)[]>(() => {
    const stores = new Set((fleet.data ?? []).map((row) => row.storeId));
    return [null, ...stores];
  }, [fleet.data]);

  const playlists = useQuery({
    queryKey: [...PLAYLISTS_QUERY_KEY, 'reachable', playlistScopes.map((s) => s ?? '').join('|')],
    queryFn: ({ signal }) => listPlaylistsAcross(playlistScopes, signal),
    enabled: dialog.kind === 'assign',
  });

  const assign = useMutation({
    mutationFn: ({ screenId, schedule }: { screenId: string; schedule: ScreenSchedule }) =>
      setScreenSchedule(screenId, schedule),
    onSuccess: async (_result, variables) => {
      await queryClient.invalidateQueries({ queryKey: FLEET_QUERY_KEY });
      await queryClient.invalidateQueries({ queryKey: screenQueryKey(variables.screenId) });
      closeDialog();
    },
  });

  const remove = useMutation({
    mutationFn: (screenId: string) => deleteScreen(screenId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: FLEET_QUERY_KEY });
      closeDialog();
    },
  });

  /**
   * Puts the install prompt on one television.
   *
   * `window` rather than `now` on purpose. The confirmation dialog covers the menu board
   * while it is up, so the default is to wait for the hour the store nominated rather
   * than to interrupt whatever is being served. "Send now" is the deliberate override,
   * for when somebody is already on the telephone to the shop.
   */
  const sendUpdate = useMutation({
    mutationFn: ({ screenId, deliver }: { screenId: string; deliver: CommandDelivery }) =>
      sendCommand(screenId, 'apply_update', deliver),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: FLEET_QUERY_KEY });
    },
  });

  const rows = useMemo(() => sortFleet(fleet.data ?? [], sort), [fleet.data, sort]);
  const tally = useMemo(() => tallyFleet(fleet.data ?? []), [fleet.data]);

  // A screen nobody has pointed at a playlist reads as online, idle and healthy,
  // which is indistinguishable from working. It is called out at the top of the
  // page rather than left to the last column of a wide table.
  const unassigned = useMemo(
    () => (fleet.data ?? []).filter((row) => row.defaultPlaylistId === null),
    [fleet.data],
  );

  return (
    <>
      <PageHeader
        title="Screens"
        description="One record per TV: what it is called, how big it is, which way it is turned on the wall, and how it is behaving right now."
      >
        <button
          type="button"
          className="button button--ghost"
          onClick={() => void fleet.refetch()}
          disabled={fleet.isFetching}
        >
          {fleet.isFetching ? 'Refreshing…' : 'Refresh'}
        </button>
        <button type="button" className="button button--primary" onClick={() => setClaiming(true)}>
          Claim a TV
        </button>
      </PageHeader>

      <InfoNote>
        Screens are not invented here. A TV shows a six-character pairing code on its own, and
        claiming that code is what creates the record; this page is where it is then named, turned
        and, if it is being retired, un-paired.
      </InfoNote>

      {unassigned.length > 0 ? (
        <section className="alert alert--warning" role="status">
          <p>
            <strong>
              {unassigned.length === 1
                ? '1 screen has no playlist.'
                : `${unassigned.length} screens have no playlist.`}
            </strong>{' '}
            A screen with nothing assigned shows its waiting card, which looks the same as a fault
            from across a room. Building a playlist does not put it on a television — this is the
            step that does.
          </p>
          <div className="alert__actions">
            {unassigned.map((row) => (
              <button
                key={row.screen.id}
                type="button"
                className="button button--primary button--small"
                disabled={!canEditScreen(user, row.storeId)}
                onClick={() => {
                  assign.reset();
                  setDialog({ kind: 'assign', row });
                }}
              >
                Choose what {row.screen.name} plays
              </button>
            ))}
          </div>
        </section>
      ) : null}

      {fleet.isPending ? <LoadingBlock label="Loading the fleet…" /> : null}

      {fleet.isError ? (
        <ErrorBlock error={fleet.error} action="load the fleet" onRetry={() => void fleet.refetch()} />
      ) : null}

      {!fleet.isPending && !fleet.isError && rows.length === 0 ? (
        // Nothing to see because no screen exists, or nothing to see because no
        // store has been assigned to this account — two different problems with
        // two different people who can fix them.
        hasNoScope(user) ? (
          <InlineEmpty
            title="No stores assigned to you yet"
            description="Screens are listed per store, and your account has not been given access to one. An administrator needs to add you before any screen appears here."
          />
        ) : (
          <InlineEmpty
            title="No screens paired yet"
            description="Install the app on a Fire TV stick, launch it, and it will show a pairing code. Claiming that code is what puts a screen on this page."
          />
        )
      ) : null}

      {rows.length > 0 ? (
        <section className="panel">
          <div className="panel__head">
            <div>
              <h3 className="panel__title">
                {tally.total === 1 ? '1 screen' : `${tally.total} screens`}
              </h3>
              <p className="panel__subtitle">
                {tally.needsAttention === 0
                  ? 'Every screen is reporting normally.'
                  : `${tally.needsAttention === 1 ? '1 screen needs' : `${tally.needsAttention} screens need`} attention — ${tally.online} online, ${tally.offline} offline, ${tally.closed} closed.`}
              </p>
              <p className="panel__note">
                Refreshed every {Math.round(FLEET_REFRESH_MS / 1000)} seconds; last read{' '}
                {formatRelativeTime(new Date(fleet.dataUpdatedAt).toISOString(), now)}.
              </p>
            </div>

            <div className="segmented" role="group" aria-label="Sort screens">
              {SORTS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className={option.value === sort ? 'segmented__item is-selected' : 'segmented__item'}
                  aria-pressed={option.value === sort}
                  onClick={() => setSort(option.value)}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th scope="col">Screen</th>
                  <th scope="col">Health</th>
                  <th scope="col">Now playing</th>
                  <th scope="col">App</th>
                  <th scope="col">Storage</th>
                  <th scope="col">
                    <span className="visually-hidden">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <FleetRow
                    key={row.screen.id}
                    row={row}
                    now={now}
                    mayEdit={canEditScreen(user, row.storeId)}
                    onEdit={() => {
                      update.reset();
                      setDialog({ kind: 'edit', row });
                    }}
                    onUpdate={() => {
                      sendUpdate.reset();
                      setDialog({ kind: 'update', row });
                    }}
                    onAssign={() => {
                      assign.reset();
                      setDialog({ kind: 'assign', row });
                    }}
                    onRemove={() => {
                      remove.reset();
                      setDialog({ kind: 'remove', row });
                    }}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      <PreviewPanel />

      <ClaimScreenDialog open={claiming} onClose={() => setClaiming(false)} />

      <Modal
        open={dialog.kind === 'update'}
        title={dialog.kind === 'update' ? `Update ${dialog.row.screen.name}` : 'Send update'}
        onClose={closeDialog}
      >
        {dialog.kind === 'update' ? (
          <div className="stack">
            <p>
              This TV has a new version downloaded and checked, waiting to be installed.
              Installing puts a box on the screen that somebody in the shop has to answer.
            </p>
            <p className="alert alert--warning" role="status">
              <strong>Cancel is highlighted, not Install.</strong> Tell whoever is there to
              press <strong>right</strong>, then <strong>select</strong>. Pressing select
              straight away cancels it, and this screen will still say an update is waiting.
            </p>
            <p className="field__hint">
              The menu board comes back by itself about eight seconds after they press it.
            </p>
            {sendUpdate.error !== null && sendUpdate.error !== undefined ? (
              <p className="alert alert--danger" role="alert">
                {errorMessage(sendUpdate.error)}
              </p>
            ) : null}
            <div className="form__actions">
              <button type="button" className="button button--ghost" onClick={closeDialog}>
                Cancel
              </button>
              <button
                type="button"
                className="button button--ghost"
                disabled={sendUpdate.isPending}
                onClick={() =>
                  sendUpdate.mutate(
                    { screenId: dialog.row.screen.id, deliver: 'window' },
                    { onSuccess: closeDialog },
                  )
                }
              >
                Send in the update window
              </button>
              <button
                type="button"
                className="button button--primary"
                disabled={sendUpdate.isPending}
                onClick={() =>
                  sendUpdate.mutate(
                    { screenId: dialog.row.screen.id, deliver: 'now' },
                    { onSuccess: closeDialog },
                  )
                }
              >
                {sendUpdate.isPending ? 'Sending…' : 'Send now'}
              </button>
            </div>
          </div>
        ) : null}
      </Modal>

      <Modal
        open={dialog.kind === 'edit'}
        size="wide"
        title={dialog.kind === 'edit' ? `Edit ${dialog.row.screen.name}` : 'Edit screen'}
        onClose={closeDialog}
      >
        {dialog.kind === 'edit' ? (
          <ScreenEditForm
            key={dialog.row.screen.id}
            screenId={dialog.row.screen.id}
            storeName={dialog.row.storeName}
            initialName={dialog.row.screen.name}
            initialRotation={dialog.row.screen.rotationDegrees}
            initialAutoStart={dialog.row.screen.autoStart}
            initialAlwaysOn={dialog.row.alwaysOn}
            busy={update.isPending}
            error={update.error}
            onSubmit={(patch) => update.mutate({ screenId: dialog.row.screen.id, patch })}
            onCancel={closeDialog}
          />
        ) : null}
      </Modal>

      <Modal
        open={dialog.kind === 'assign'}
        title={
          dialog.kind === 'assign'
            ? `What ${dialog.row.screen.name} plays`
            : 'What this screen plays'
        }
        onClose={closeDialog}
      >
        {dialog.kind === 'assign' ? (
          playlists.isPending ? (
            <LoadingBlock label="Loading playlists…" />
          ) : playlists.isError ? (
            <ErrorBlock
              error={playlists.error}
              action="load the playlists"
              onRetry={() => void playlists.refetch()}
            />
          ) : (
            <AssignPlaylistForm
              key={dialog.row.screen.id}
              screenName={dialog.row.screen.name}
              playlists={(playlists.data ?? []).filter(
                (playlist) =>
                  playlist.storeId === null || playlist.storeId === dialog.row.storeId,
              )}
              currentPlaylistId={null}
              busy={assign.isPending}
              error={assign.error}
              onSubmit={(schedule) =>
                assign.mutate({ screenId: dialog.row.screen.id, schedule })
              }
              onCancel={closeDialog}
            />
          )
        ) : null}
      </Modal>

      <ConfirmDialog
        open={dialog.kind === 'remove'}
        title={dialog.kind === 'remove' ? `Un-pair ${dialog.row.screen.name}?` : 'Un-pair screen?'}
        description="The device token is deleted, so the TV loses access on its next heartbeat, clears its cache and goes back to showing a pairing code. Pairing it again creates a new screen record."
        confirmLabel="Un-pair and delete"
        busyLabel="Removing…"
        busy={remove.isPending}
        error={remove.error === null ? null : errorMessage(remove.error)}
        onConfirm={() => {
          if (dialog.kind === 'remove') remove.mutate(dialog.row.screen.id);
        }}
        onClose={closeDialog}
      />
    </>
  );
}

interface FleetRowProps {
  row: FleetScreen;
  now: number;
  mayEdit: boolean;
  onEdit: () => void;
  onUpdate: () => void;
  onAssign: () => void;
  onRemove: () => void;
}

function FleetRow({ row, now, mayEdit, onEdit, onUpdate, onAssign, onRemove }: FleetRowProps): ReactElement {
  const flags = screenFlags(row);

  return (
    <tr>
      <th scope="row" className="data-table__name">
        <span className="data-table__primary">{row.screen.name}</span>
        <span className="data-table__secondary">
          {row.storeName} · {rotationLabel(row.screen.rotationDegrees)}
        </span>
      </th>

      <td>
        <span className={`chip chip--${healthTone(row.health)}`} title={healthExplanation(row.health)}>
          {healthLabel(row.health)}
        </span>
        <span
          className="data-table__secondary"
          title={formatAbsoluteTime(row.lastHeartbeatAt)}
        >
          Seen {formatRelativeTime(row.lastHeartbeatAt, now)}
        </span>
        {flags.length > 0 ? (
          <span className="flag-list">
            {flags.map((flag) => (
              <span key={flag.label} className={`chip chip--${flag.tone}`} title={flag.detail}>
                {flag.label}
              </span>
            ))}
          </span>
        ) : null}
      </td>

      <td>
        <span className="data-table__primary">{playbackStateLabel(row.playbackState)}</span>
        <span className="data-table__secondary mono">
          {row.currentAssetId ?? 'No item reported'}
        </span>
      </td>

      <td>{formatAppVersion(row.appVersionCode)}</td>

      <td>{storageSummary(row)}</td>

      <td className="data-table__actions">
        <button
          type="button"
          className="button button--ghost button--small"
          onClick={onAssign}
          disabled={!mayEdit}
          title={mayEdit ? undefined : 'This screen belongs to a store you do not manage.'}
        >
          Playlist
        </button>
        <button
          type="button"
          className="button button--ghost button--small"
          onClick={onEdit}
          disabled={!mayEdit}
          title={mayEdit ? undefined : 'This screen belongs to a store you do not manage.'}
        >
          Edit
        </button>
        {row.updateState === 'staged' ? (
          <button
            type="button"
            className="button button--ghost button--small"
            onClick={onUpdate}
            disabled={!mayEdit}
            title={
              mayEdit
                ? 'Puts the install prompt on this TV. Somebody there presses right, then select.'
                : 'This screen belongs to a store you do not manage.'
            }
          >
            Update
          </button>
        ) : null}
        <button
          type="button"
          className="button button--ghost button--small"
          onClick={onRemove}
          disabled={!mayEdit}
          title={mayEdit ? undefined : 'This screen belongs to a store you do not manage.'}
        >
          Un-pair
        </button>
      </td>
    </tr>
  );
}

/**
 * The rotation and fit simulator. It runs the shared resolver against a built-in
 * manifest; binding it to a live screen's own content waits on the media library.
 */
function PreviewPanel(): ReactElement {
  const [rotation, setRotation] = useState<RotationDegrees>(0);
  const [resolutionIndex, setResolutionIndex] = useState(0);
  const [scenario, setScenario] = useState<PreviewScenario>('playing');

  const resolution = RESOLUTIONS[resolutionIndex]?.value ?? RESOLUTIONS[0]?.value;
  const manifest = useMemo(() => sampleManifest(scenario, rotation), [scenario, rotation]);

  return (
    <section className="panel">
      <div className="panel__head">
        <div>
          <h3 className="panel__title">Preview</h3>
          <p className="panel__subtitle">
            The simulator runs the shared resolver against a manifest, exactly as the TV app does:
            real item durations, real fit modes, real rotation. It is driven by a built-in sample
            manifest until the media library binds it to a live screen.
          </p>
        </div>
      </div>

      <div className="control-row">
        <label className="field field--inline">
          <span className="field__label">Rotation</span>
          <select
            className="input"
            value={rotation}
            onChange={(event) => setRotation(Number(event.currentTarget.value) as RotationDegrees)}
          >
            {([0, 90, 180, 270] as RotationDegrees[]).map((value) => (
              <option key={value} value={value}>
                {value}° — {rotationLabel(value)}
              </option>
            ))}
          </select>
        </label>

        <label className="field field--inline">
          <span className="field__label">Reported resolution</span>
          <select
            className="input"
            value={resolutionIndex}
            onChange={(event) => setResolutionIndex(Number(event.currentTarget.value))}
          >
            {RESOLUTIONS.map((option, index) => (
              <option key={option.label} value={index}>
                {option.label}
              </option>
            ))}
          </select>
        </label>

        <div className="field field--inline">
          <span className="field__label">Screen state</span>
          <div className="segmented" role="group" aria-label="Screen state">
            {SCENARIOS.map((option) => (
              <button
                key={option.value}
                type="button"
                className={option.value === scenario ? 'segmented__item is-selected' : 'segmented__item'}
                aria-pressed={option.value === scenario}
                onClick={() => setScenario(option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {resolution !== undefined ? (
        <ScreenPreview manifest={manifest} rotationDegrees={rotation} resolution={resolution} />
      ) : null}

      <p className="panel__note">
        The frame is the picture the stick sends down the HDMI cable, which is always landscape —
        Fire TV never rotates its own output. On a portrait screen the app turns its canvas inside
        that frame, so the content lies on its side here and stands upright once the TV is turned on
        the wall.
      </p>
    </section>
  );
}
